import React from 'react'
import { Box, Typography, Chip, Tooltip, useMediaQuery, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import CardActionButton from 'components/global/CardActions/CardActionButton'
import { IPTVChannel } from '../types'
import { IColumn, ICtx, IItem } from 'packages/ui/Table/models'
import { createExpandedRowComponent, useResponsiveTableContext } from 'packages/ui/Table'

interface ActionsProps {
  id: string
  ctx?: ICtx
}

interface ChannelLogoProps {
  channel: IPTVChannel
}

const Status = ({ isError }: { isError: boolean }) => {
  const { t } = useTranslation()
  return (
    <Tooltip title={isError ? t('iptv.status.unreachable') : t('iptv.status.operational')}>
      <Box
        sx={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: isError ? 'error.main' : 'success.main',
          color: 'white'
        }}
      >
        <i className={`fa-solid fa-${isError ? 'xmark' : 'check'}`} style={{ fontSize: '10px' }} />
      </Box>
    </Tooltip>
  )
}

const TypeIcon = ({ type }: { type: string }) => (
  <i className={`fa-light fa-${(type?.toLowerCase() === 'media') ? 'tv' : 'radio'}`} />
)

const Actions: React.FC<ActionsProps> = ({ id }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const responsiveTableContext = useResponsiveTableContext()

  return (
    <Box display={'flex'} gap={2}>
      <CardActionButton
        iconlabel={'eye'}
        tooltiptitle={t('iptv.table.tooltip_view_button')}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation()
          navigate(`/iptv/${id}`)
        }}
      />
      {isMobile && responsiveTableContext && (
        <CardActionButton
          tooltiptitle={responsiveTableContext.isRowExpanded(id) ? t('iptv.actions.collapse_details') : t('iptv.actions.expand_details')}
          iconlabel={responsiveTableContext.isRowExpanded(id) ? 'chevron-up' : 'chevron-down'}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation()
            responsiveTableContext.toggleRowExpansion(id)
          }}
        />
      )}
    </Box>
  )
}

const ChannelLogo: React.FC<ChannelLogoProps> = ({ channel }) => (
  <Box display="flex" alignItems="center" gap={2}>
    <img src={channel.logo} alt={channel.name} style={{ width: 40, height: 40, objectFit: 'contain' }} />
    <Typography variant="body2">{channel.name}</Typography>
  </Box>
)

export const ExpandedRowDetails = createExpandedRowComponent<IPTVChannel>((channel, t) => [
  {
    key: 'ip_address',
    label: t('iptv.table.ip_address'),
    value: channel.ip_address || '-'
  },
  {
    key: 'port',
    label: t('iptv.table.port'),
    value: channel.port || '-'
  },
  {
    key: 'type',
    label: t('iptv.table.type'),
    value: channel.type || '-'
  },
  {
    key: 'quality',
    label: t('iptv.table.quality'),
    value: channel.quality || '-'
  }
])

const COLUMNS_CONFIG: IColumn[] = [
  {
    name: 'logo_name',
    header: {
      label: (ctx: ICtx) => ctx.t('iptv.table.logo_name')
    },
    value: (item: IItem) => <ChannelLogo channel={item as IPTVChannel} />,
    sortable: true,
    sortBy: 'name',
    priority: 1
  },
  {
    name: 'position',
    header: {
      label: (ctx: ICtx) => ctx.t('iptv.table.channel')
    },
    value: (item: IItem) => (item as IPTVChannel).position.toString(),
    sortable: true,
    priority: 1
  },
  {
    name: 'status',
    header: {
      label: (ctx: ICtx) => ctx.t('iptv.table.status')
    },
    value: (item: IItem) => <Status isError={(item as IPTVChannel).has_errors} />,
    sortable: true,
    sortBy: 'status',
    priority: 1
  },
  {
    name: 'ip_address',
    header: {
      label: (ctx: ICtx) => ctx.t('iptv.table.ip_address')
    },
    value: (item: IItem) => (item as IPTVChannel).ip_address,
    priority: 3
  },
  {
    name: 'port',
    header: {
      label: (ctx: ICtx) => ctx.t('iptv.table.port')
    },
    value: (item: IItem) => (item as IPTVChannel).port,
    priority: 3
  },
  {
    name: 'type',
    header: {
      label: (ctx: ICtx) => ctx.t('iptv.table.type')
    },
    value: (item: IItem) => <TypeIcon type={(item as IPTVChannel).type} />,
    priority: 2
  },
  {
    name: 'quality',
    header: {
      label: (ctx: ICtx) => ctx.t('iptv.table.quality')
    },
    value: (item: IItem) => (
      <Chip
        label={(item as IPTVChannel).quality}
        sx={{
          bgcolor: 'surface.secondary',
          borderRadius: '6px',
          padding: '2px 2px'
        }}
      />
    ),
    priority: 2
  },
  {
    name: 'actions',
    header: {
      label: (ctx: ICtx) => ctx.t('iptv.table.actions'),
      className: 'actions'
    },
    value: (item: IItem, ctx?: ICtx) => <Actions id={(item as IPTVChannel).id} ctx={ctx} />,
    priority: 1
  }
]

export default COLUMNS_CONFIG
